import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Alert,
  Avatar, Divider, Grid, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  Pagination, Skeleton,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LinkIcon from '@mui/icons-material/Link';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';

const formatEur = (v) => {
  const n = parseFloat(v) || 0;
  return n.toFixed(2).replace('.', ',') + ' EUR';
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusChip = (s) => {
  const map = {
    paid: { label: 'Paye', color: 'success' },
    processing: { label: 'En cours', color: 'warning' },
    failed: { label: 'Echoue', color: 'error' },
    pending: { label: 'En attente', color: 'default' },
  };
  const c = map[s] || { label: s, color: 'default' };
  return <Chip label={c.label} color={c.color} size="small" />;
};

const StripeConnectSection = ({ commissionRate = 5, commissionLabel = 'par transaction' }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  useEffect(() => {
    api.get('/connect/status').then(r => setStatus(r.data)).catch(() => {});
    api.get('/connect/my-earnings').then(r => setEarnings(r.data?.earnings)).catch(() => {});
  }, []);

  useEffect(() => {
    setPayoutsLoading(true);
    api.get(`/connect/my-payouts?page=${payoutsPage}&limit=10`)
      .then(r => {
        setPayouts(r.data?.payouts || []);
        setPayoutsTotal(r.data?.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setPayoutsLoading(false));
  }, [payoutsPage]);

  const handleOnboard = async () => {
    setLoading(true);
    try {
      const res = await api.post('/connect/onboard');
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setDashboardLoading(true);
    try {
      const res = await api.get('/connect/dashboard-link');
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDashboardLoading(false);
    }
  };

  return (
    <Box>
      {/* -- Earnings Summary Cards -- */}
      {earnings && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)', color: '#fff' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Aujourd'hui</Typography>
                <Typography variant="h5" fontWeight={700}>{formatEur(earnings.today_net)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {earnings.today_orders} course{earnings.today_orders > 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Total gagne</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">{formatEur(earnings.total_earned)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {earnings.total_orders} course{earnings.total_orders > 1 ? 's' : ''} total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Commission Usafiri</Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">{formatEur(earnings.total_commission)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {commissionRate}% {commissionLabel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Virements</Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">{earnings.paid_count}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {earnings.failed_count > 0 ? `${earnings.failed_count} echoue(s)` : 'Tous reussis'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* -- Stripe Connect Status -- */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ bgcolor: '#635bff', color: '#fff', width: 50, height: 50 }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Recevoir mes paiements
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configurez votre compte Stripe Connect pour recevoir vos gains directement sur votre compte bancaire.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {status?.onboarding_done ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Votre compte Stripe est configure et actif. Vos gains sont vires automatiquement chaque jour.
                  </Alert>
                  <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <Chip label="Paiements actifs" color="success" />
                    {status.charges_enabled && <Chip label="Charges activees" color="success" variant="outlined" />}
                    {status.payouts_enabled && <Chip label="Virements actifs" color="success" variant="outlined" />}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={dashboardLoading ? <CircularProgress size={16} /> : <OpenInNewIcon />}
                      onClick={handleOpenDashboard}
                      disabled={dashboardLoading}
                    >
                      Voir mon dashboard Stripe
                    </Button>
                  </Box>
                </Box>
              ) : status?.connected ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Votre inscription Stripe n'est pas terminee. Completez-la pour recevoir vos paiements.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                    onClick={handleOnboard}
                    disabled={loading}
                    sx={{ background: 'linear-gradient(135deg, #635bff 0%, #4f46e5 100%)' }}
                  >
                    Finaliser mon compte Stripe
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Pour recevoir vos gains, vous devez configurer un compte Stripe Connect. C'est gratuit et prend moins de 5 minutes.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                    onClick={handleOnboard}
                    disabled={loading}
                    sx={{ background: 'linear-gradient(135deg, #635bff 0%, #4f46e5 100%)' }}
                  >
                    Configurer Stripe Connect
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* -- How it works -- */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Comment ca marche ?
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  1. Configurez votre compte Stripe (identite + RIB)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  2. Exercez votre activite normalement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  3. Chaque jour, vos gains nets sont vires automatiquement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  4. Commission Usafiri : {commissionRate}% {commissionLabel}
                </Typography>
              </Box>

              {earnings?.last_payout && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Dernier virement
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {formatEur(earnings.last_payout.net_amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(earnings.last_payout.period_start)} - {formatDate(earnings.last_payout.period_end)}
                      </Typography>
                    </Box>
                    {statusChip(earnings.last_payout.status)}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* -- Payout History Table -- */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ReceiptLongIcon color="action" />
            <Typography variant="h6" fontWeight={700}>
              Historique des virements
            </Typography>
          </Box>

          {payoutsLoading ? (
            <Box>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : payouts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TrendingUpIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Aucun virement pour le moment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Vos virements apparaitront ici apres votre premiere journee de livraisons
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Periode</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Courses</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Brut</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Commission</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Net</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Statut</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payouts.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(p.period_start)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            au {formatDate(p.period_end)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={p.order_count} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatEur(p.gross_amount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main">
                            -{formatEur(p.commission_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {formatEur(p.net_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {statusChip(p.status)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {p.paid_at ? formatDate(p.paid_at) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {payoutsTotal > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={payoutsTotal}
                    page={payoutsPage}
                    onChange={(e, v) => setPayoutsPage(v)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StripeConnectSection;
